'use client';

/**
 * InterviewStageView Component
 *
 * Tool-based interview interface for requirements gathering.
 * Uses structured question types (single-select, multi-select, etc.)
 * instead of free-form chat for a better UX.
 *
 * @see Issue #224: Create InterviewPage with chat interface
 */

import type { Request } from '@cortex/database';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Heading,
  Inline,
  Stack,
  Text,
} from '@cortex/ui';
import { CheckCircle, Code, MessageSquare, RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';

import { InterviewContainer } from '@/components/interview/InterviewContainer';
import { useFetchWithAuth } from '@/hooks/use-api';
import { useUpdateRequestStatus } from '@/hooks/use-request-queries';

/** Stage view props */
interface StageViewProps {
  request: Request;
}

interface InterviewStageViewProps extends StageViewProps {
  onRequestUpdate: (request: Request) => void;
}

/** Interview completion outputs */
interface InterviewOutputs {
  gherkinCriteria: string;
  agentPrompt: string;
  summary: string;
}

export default function InterviewStageView({
  request,
  onRequestUpdate,
}: InterviewStageViewProps) {
  const fetchWithAuth = useFetchWithAuth();
  const updateStatus = useUpdateRequestStatus();
  const [error, setError] = useState<string | null>(null);

  // Interview completion state
  const [isCompleting, setIsCompleting] = useState(false);
  const [interviewOutputs, setInterviewOutputs] =
    useState<InterviewOutputs | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  // Handle interview completion
  const handleInterviewComplete = useCallback(async () => {
    setIsCompleting(true);
    setError(null);

    try {
      const response = await fetchWithAuth(
        `/requests/${request.id}/interview/complete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error?.message || 'Failed to complete interview'
        );
      }

      const responseData = await response.json();
      // API wraps response in { data: ... } structure
      setInterviewOutputs(responseData.data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to complete interview';
      setError(errorMessage);
    } finally {
      setIsCompleting(false);
    }
  }, [request.id, fetchWithAuth]);

  // Handle starting development
  const handleStartDevelopment = useCallback(async () => {
    setError(null);

    try {
      await updateStatus.mutateAsync({
        requestId: request.id,
        status: 'develop',
      });

      // Update parent state to trigger UI change
      onRequestUpdate({ ...request, status: 'develop' });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to start development';
      setError(errorMessage);
    }
  }, [request, onRequestUpdate, updateStatus]);

  // Handle resetting the interview
  const handleResetInterview = useCallback(async () => {
    setShowResetModal(false);
    setIsResetting(true);
    setError(null);

    try {
      const response = await fetchWithAuth(
        `/requests/${request.id}/interview/reset`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error?.message || 'Failed to reset interview'
        );
      }

      // Reset local state and update parent
      setInterviewOutputs(null);
      onRequestUpdate({ ...request, status: 'create' });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to reset interview';
      setError(errorMessage);
    } finally {
      setIsResetting(false);
    }
  }, [request, onRequestUpdate, fetchWithAuth]);

  // Show acceptance criteria preview if interview is complete
  if (interviewOutputs) {
    return (
      <Stack gap="lg">
        {/* Success Header */}
        <Alert variant="success" className="p-6">
          <Inline gap="sm" align="center">
            <CheckCircle className="text-success h-6 w-6" />
            <Heading level={2} className="text-lg font-semibold">
              Interview Complete
            </Heading>
          </Inline>
          <Text color="secondary" size="sm" className="mt-2">
            {interviewOutputs.summary}
          </Text>
        </Alert>

        {/* Acceptance Criteria Preview */}
        <Card className="p-6">
          <Heading level={3} className="font-semibold">
            Generated Acceptance Criteria
          </Heading>
          <Text color="muted" size="sm" className="mt-1">
            These criteria will be used to validate the implementation.
          </Text>

          <Card className="bg-surface-2 mt-4">
            <CardContent className="p-4">
              <Text
                as="div"
                className="text-foreground font-mono text-sm whitespace-pre-wrap"
              >
                {interviewOutputs.gherkinCriteria}
              </Text>
            </CardContent>
          </Card>
        </Card>

        {/* Error message */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Action Buttons */}
        <Inline justify="end" gap="sm">
          <Button
            variant="outline"
            onClick={() => setInterviewOutputs(null)}
            disabled={updateStatus.isPending}
          >
            Back to Interview
          </Button>
          <Button
            onClick={() => void handleStartDevelopment()}
            loading={updateStatus.isPending}
          >
            {!updateStatus.isPending && <Code className="mr-2 h-4 w-4" />}
            {updateStatus.isPending ? 'Starting...' : 'Start Development'}
          </Button>
        </Inline>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {/* Header */}
      <Card className="p-4">
        <Inline justify="between" align="center">
          <Stack gap="xs">
            <Inline gap="sm" align="center">
              <MessageSquare className="text-primary h-5 w-5" />
              <Heading level={2} className="font-semibold">
                Interview in Progress
              </Heading>
            </Inline>
            <Text color="secondary" size="sm" className="mt-1">
              Answer the questions to help create clear acceptance criteria.
            </Text>
          </Stack>

          {/* Reset Button */}
          <Button
            onClick={() => setShowResetModal(true)}
            disabled={isCompleting}
            loading={isResetting}
            variant="ghost"
            size="sm"
            title="Reset interview and start over"
          >
            {!isResetting && <RotateCcw className="mr-2 h-4 w-4" />}
            {isResetting ? 'Resetting...' : 'Reset'}
          </Button>
        </Inline>
      </Card>

      {/* Error message */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Interview Container - Tool-based question UI */}
      <Card className="p-6">
        <InterviewContainer
          requestId={request.id}
          onComplete={() => void handleInterviewComplete()}
        />
      </Card>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={showResetModal}
        onOpenChange={(isOpen) => {
          if (!isOpen) setShowResetModal(false);
        }}
      >
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Reset Interview</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack gap="sm">
              <Text color="secondary">
                Are you sure you want to reset this interview?
              </Text>
              <Text color="muted" size="sm">
                All conversation history will be permanently deleted and
                you&apos;ll start fresh with a new interview session.
              </Text>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetModal(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleResetInterview()}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset Interview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
